// Task-detail affordances enabled per chain, as served by
// `GET /resources/task_details_features`.

import { memoizeLastCall } from '../utils';

/**
 * Feature names as they arrive in the `task_details_features` response list.
 */
export const TASK_DETAILS_FEATURES = {
  BARCODE: 'barcode',
};

// Bucket segment that identifies a generated barcode URL inside `/images`.
const BARCODE_BUCKET_SEGMENT = '/zippedi_barcodes/';

const NO_IMAGES = [];

/**
 * Returns whether a feature is enabled for the current chain and task type.
 *
 * Membership check, never equality: the column is a generic list and future
 * affordances arrive as extra entries.
 *
 * @param {string[]|null|undefined} features Feature list from the API.
 * @param {string} feature Feature name to look for.
 * @returns {boolean} True when the list contains the feature.
 */
export function hasTaskDetailsFeature(features, feature) {
  return Array.isArray(features) && features.includes(feature);
}

/**
 * Resolve the displayable URL of an entry in the `/images` list.
 *
 * The list is heterogeneous: crops and barcodes arrive as plain strings, while
 * overhead entries are objects carrying metadata. Base64 payloads have no URL.
 *
 * @param {object|string|null|undefined} image One entry of the images list.
 * @returns {string|null} The URL, or null when the entry has none.
 */
export function getImageUrl(image) {
  if (!image) return null;
  if (typeof image === 'string') return image;
  if (typeof image.url === 'string') return image.url;
  if (typeof image.url === 'object' && image.url) return image.url.imageUrl ?? null;
  return null;
}

/**
 * Returns whether an images-list entry is the generated barcode.
 *
 * Compares the path only: the signed URL's query string is regenerated on
 * every refresh.
 *
 * @param {object|string|null|undefined} image One entry of the images list.
 * @returns {boolean} True when the entry points at the barcode bucket.
 */
export function isBarcodeImage(image) {
  const url = getImageUrl(image);
  if (!url) return false;
  const path = url.split('?')[0];
  return path.includes(BARCODE_BUCKET_SEGMENT);
}

/**
 * Split the images list into what the carousel shows and the barcode behind
 * the button.
 *
 * When the feature is disabled the list is returned untouched and `barcodeUrl`
 * is null. When it is enabled the barcode leaves the carousel, which can empty
 * the list; callers must then skip the carousel instead of rendering an empty
 * frame.
 *
 * Memoized on its last arguments so consumers receive a stable reference while
 * the store's `images` has not changed.
 *
 * @param {Array|null|undefined} images The `/images` response held in the store.
 * @param {boolean} barcodeAsButton Whether the barcode feature is enabled.
 * @returns {{carouselImages: Array, barcodeUrl: string|null}} Carousel entries
 *   and the barcode URL, or null when there is no barcode to show.
 */
export const splitBarcodeImages = memoizeLastCall((images, barcodeAsButton) => {
  const list = Array.isArray(images) ? images : NO_IMAGES;

  if (!barcodeAsButton) return { carouselImages: list, barcodeUrl: null };

  const carouselImages = [];
  let barcodeUrl = null;

  list.forEach(image => {
    if (isBarcodeImage(image)) {
      if (barcodeUrl === null) barcodeUrl = getImageUrl(image);
      return;
    }
    carouselImages.push(image);
  });

  return { carouselImages, barcodeUrl };
});

/**
 * Returns whether the task-detail carousel should be rendered at all.
 *
 * The carousel is only skipped when the list is empty because the barcode was
 * pulled out of it; a task with no assets at all keeps its current treatment.
 *
 * @param {Array} carouselImages Images left after the barcode was removed.
 * @param {string|null} barcodeUrl The extracted barcode URL, if any.
 * @returns {boolean} True when the carousel has something to show.
 */
export function shouldRenderCarousel(carouselImages, barcodeUrl) {
  if (carouselImages && carouselImages.length > 0) return true;
  return !barcodeUrl;
}
